import { Col, ColorPicker, Divider, Form, Input, Row, Select, Skeleton, Typography } from "antd";
import { DefaultButton } from "components/Shared/DefaultButton";
import { EntityGroupLabel } from "components/Shared/Labels/EntityGroupLabel";
import { ActAsTypes } from "enums/actAsTypes.ts";
import { AgeGroups } from "enums/ageGroups";
import { EntityStatuses } from "enums/entityStatuses";
import { Genders } from "enums/genders";
import { ImageTypes } from "enums/imageTypes";
import { useArrayDropdown } from "hooks/useArrayDropdown";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useEnumDropdown } from "hooks/useEnumDropdown";
import { useSportConfiguration } from "hooks/useSportConfiguration";
import { cloneDeep } from "lodash-es";
import { useIntl } from "react-intl";
import { getActAsTypes } from "utils/getActAsTypes.ts";
import { ImageUploader } from "./ImageUploader";

type Props = {
  isLoading: boolean;
  isUpdating: boolean;
  entity: any;
  entityGroups: Array<any>;
  venues: Array<any>;
  onSave: (data: any) => void;
};

export const EntityEditForm = ({ isLoading, isUpdating, entity, entityGroups, venues, onSave }: Props) => {
  const { formatMessage } = useIntl();
  const { configuration } = useSportConfiguration();
  const dropDownGenders = useEnumDropdown(Genders, "gender", false);
  const dropDownEntityStatuses = useEnumDropdown(EntityStatuses, "entityStatus", false);
  const dropDownAgeGroups = useEnumDropdown(AgeGroups, "ageGroup", false);
  const dropDownEntityStandards = useArrayDropdown(configuration.entityStandards, "standard", false);
  const dropDownDisciplines = useArrayDropdown(configuration.entityDisciplines, "discipline", false);
  const [form] = Form.useForm();
  const currentUser = useCurrentUser();

  const isTeamFieldsDisabled = !getActAsTypes(currentUser.roles).includes(ActAsTypes.Admin);

  const onUpdateButtonClicked = async () => {
    const fields = await form.validateFields();
    onSave(cloneDeep(fields));
  };

  if (isLoading) {
    return <Skeleton paragraph={{ rows: 8 }} />;
  }

  return (
    <>
      <Col xxl={19} xl={17} lg={15}>
        <Form form={form} layout="vertical" initialValues={entity}>
          <Typography.Text type="secondary" strong>
            {formatMessage({ id: "entityEditPage.entityDetails.title" })}
          </Typography.Text>
          <Divider className="mt-5" />
          <Row gutter={20}>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({
                  id: "entityEditPage.fields.nameFullLocal",
                })}
                name="nameFullLocal"
                rules={[{ required: true }]}
              >
                <Input disabled={isTeamFieldsDisabled} />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                rules={[{ max: 5 }]}
                label={formatMessage({ id: "entityEditPage.fields.codeLocal" })}
                name="codeLocal"
              >
                <Input disabled={isTeamFieldsDisabled} />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({
                  id: "entityEditPage.fields.nameFullLatin",
                })}
                name="nameFullLatin"
              >
                <Input disabled={isTeamFieldsDisabled} />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                rules={[{ max: 5 }]}
                label={formatMessage({ id: "entityEditPage.fields.codeLatin" })}
                name="codeLatin"
              >
                <Input disabled={isTeamFieldsDisabled} />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item label={formatMessage({ id: "entityEditPage.fields.gender" })} name="gender">
                <Select showSearch options={dropDownGenders} disabled={isTeamFieldsDisabled} />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item label={formatMessage({ id: "entityEditPage.fields.standard" })} name="standard">
                <Select showSearch options={dropDownEntityStandards} disabled={isTeamFieldsDisabled} />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({ id: "entityEditPage.fields.status" })}
                name="status"
                rules={[{ required: true }]}
              >
                <Select showSearch options={dropDownEntityStatuses} disabled={isTeamFieldsDisabled} />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({
                  id: "entityEditPage.fields.entityGroup",
                })}
                name="entityGroupId"
              >
                <Select
                  showSearch
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  optionRender={({ data }) => <EntityGroupLabel title={data?.label} imageUrl={data?.img} />}
                  options={entityGroups?.map((x) => {
                    return {
                      value: x?.entityGroupId,
                      label: x?.nameFullLocal,
                      img: x?.images?.filter((img) => img?.imageType === ImageTypes.Logo)?.[0]?.url,
                    };
                  })}
                  disabled={isTeamFieldsDisabled}
                />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({
                  id: "entityEditPage.fields.internationalReference",
                })}
                name="internationalReference"
              >
                <Input disabled={isTeamFieldsDisabled} />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({
                  id: "entityEditPage.fields.defaultVenue",
                })}
                name="defaultVenueId"
              >
                <Select
                  showSearch
                  filterOption={(input, option) => `${option?.label ?? ""}`.toLowerCase().includes(input.toLowerCase())}
                  options={venues?.map((venue) => {
                    return {
                      value: venue?.venueId,
                      label: venue?.nameLocal,
                    };
                  })}
                  disabled={isTeamFieldsDisabled}
                />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item label={formatMessage({ id: "entityEditPage.fields.ageGroup" })} name="ageGroup">
                <Select showSearch disabled={isTeamFieldsDisabled} options={dropDownAgeGroups} />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({
                  id: "entityEditPage.fields.discipline",
                })}
                name="discipline"
              >
                <Select showSearch disabled={isTeamFieldsDisabled} options={dropDownDisciplines} />
              </Form.Item>
            </Col>
          </Row>
          <Typography.Text type="secondary" strong>
            {formatMessage({ id: "entityEditPage.colorsDetails.title" })}
          </Typography.Text>
          <Divider className="mt-5" />
          <Row>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({
                  id: "entityEditPage.fields.colors.primary",
                })}
                name={["colors", "primary"]}
              >
                <ColorPicker />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({
                  id: "entityEditPage.fields.colors.secondary",
                })}
                name={["colors", "secondary"]}
              >
                <ColorPicker />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={formatMessage({
                  id: "entityEditPage.fields.colors.tertiary",
                })}
                name={["colors", "tertiary"]}
              >
                <ColorPicker />
              </Form.Item>
            </Col>
          </Row>
          <Typography.Text type="secondary" strong>
            {formatMessage({ id: "entityEditPage.socialDetails.title" })}
          </Typography.Text>
          <Divider className="mt-5" />
          <Row gutter={20}>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={
                  <span>
                    <i className="fa-brands fa-facebook" />{" "}
                    {formatMessage({
                      id: "entityEditPage.fields.social.facebook",
                    })}
                  </span>
                }
                name={["social", "facebook"]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={
                  <span>
                    <i className="fa-brands fa-instagram" />{" "}
                    {formatMessage({
                      id: "entityEditPage.fields.social.instagram",
                    })}
                  </span>
                }
                name={["social", "instagram"]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={
                  <span>
                    <i className="fa-brands fa-jira" />{" "}
                    {formatMessage({
                      id: "entityEditPage.fields.social.ticketing",
                    })}
                  </span>
                }
                name={["social", "ticketing"]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={
                  <span>
                    <i className="fa-brands fa-tiktok" />{" "}
                    {formatMessage({
                      id: "entityEditPage.fields.social.tiktok",
                    })}
                  </span>
                }
                name={["social", "tiktok"]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={
                  <span>
                    <i className="fa-brands fa-twitter" />{" "}
                    {formatMessage({
                      id: "entityEditPage.fields.social.twitter",
                    })}
                  </span>
                }
                name={["social", "twitter"]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={
                  <span>
                    <i className="fa-solid fa-globe" />{" "}
                    {formatMessage({
                      id: "entityEditPage.fields.social.website",
                    })}
                  </span>
                }
                name={["social", "website"]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={8} lg={8} xl={6}>
              <Form.Item
                label={
                  <span>
                    <i className="fa-brands fa-wikipedia-w" />{" "}
                    {formatMessage({
                      id: "entityEditPage.fields.social.wikipedia",
                    })}
                  </span>
                }
                name={["social", "wikipedia"]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Row justify="center">
          <DefaultButton loading={isUpdating} onClick={onUpdateButtonClicked} size="large">
            {formatMessage({ id: "button.update" })}
          </DefaultButton>
        </Row>
      </Col>
      <Col xxl={5} xl={7} lg={9}>
        <ImageUploader entity={entity} readonly={isTeamFieldsDisabled} />
      </Col>
    </>
  );
};
